'use client'

import { useCallback } from 'react'
import { useTranslation } from '@/lib/i18n'
import { ENGLISH_MESSAGES } from '@/lib/i18n/catalogues'
import { translate } from '@/lib/i18n/translate'

export type ShellTranslate = (key: string, params?: Record<string, string | number>) => string

/**
 * `t` for the app shell: the sidebar, the navigation, the shared data table.
 *
 * Inside the I18nProvider it is the provider's `t`, in the interface language.
 * Outside it (the provider's default context answers in English too), and
 * under a test stub that echoes the key, it answers in English: the shell
 * renders where the provider does not reach (specs render the data table
 * bare, and dozens of them assert its English wording; portals mount
 * elsewhere), and many specs mock `useTranslation` with `(k) => k`. A raw key
 * path is never a label; English is. A key no catalogue holds is still
 * returned as is, so a real mistake stays visible.
 *
 * `locale` is the language the words are in; 'en' whenever the context has
 * none (outside the provider, or a mock that omits it).
 */
export function useShellTranslation(): { t: ShellTranslate; locale: string } {
  const { t, locale } = useTranslation()
  const shellT = useCallback<ShellTranslate>(
    (key, params) => {
      const text = t(key, params)
      return text === key ? translate(undefined, ENGLISH_MESSAGES, key, params) : text
    },
    [t],
  )
  return { t: shellT, locale: locale ?? 'en' }
}
